import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import type { CaseCptLine, Laterality, ScheduledCase, SurgeonTemplate } from '@/lib/templates/types';
import {
  clinicalSeverityEvidenceText,
  hasAscExclusionSiteOfCareJustification,
  hasKneeMuaFunctionalLimitation,
  hasKneeMuaRomEvidence,
  hasKneeMuaTherapyCourse,
  hasShoulderFormalTherapyFailure,
} from './evidenceValidation';

export type PreAuthAuthoringSeverity = 'satisfied' | 'guidance' | 'ask_surgeon' | 'blocker';

export interface PreAuthAuthoringItem {
  id: string;
  severity: PreAuthAuthoringSeverity;
  title: string;
  finding: string;
  prevention: string;
  payerLogic: string;
  affectedCodes?: string[];
  recommendedCpt?: CaseCptLine;
  suggestedLanguage?: string;
}

export interface PreAuthAuthoringAudit {
  status: 'ready' | 'needs_surgeon' | 'blocked';
  score: number;
  summary: string;
  items: PreAuthAuthoringItem[];
  blockers: PreAuthAuthoringItem[];
  surgeonQuestions: PreAuthAuthoringItem[];
  canGeneratePreAuth: boolean;
}

const PATELLAR_REALIGNMENT_TERMS = [
  'patella alta',
  'patella baja',
  'patellar subluxation',
  'patella subluxation',
  'lateral subluxation',
  'medial subluxation',
  'patellar malalignment',
  'patella malalignment',
  'maltracking',
  'dislocating patella',
  'recurrent patellar dislocation',
  'tibial tubercle',
  'realignment',
];

const EXTENSOR_MECHANISM_TERMS = [
  'extensor mechanism',
  'quadriceps',
  'vmo',
  'straight leg raise',
  'extensor lag',
  'avulsion',
  'tendon disruption',
  'tendon tear',
];

const ACUTE_TRAUMA_TERMS = [
  'acute',
  'traumatic',
  'fall',
  'injury',
  'urgent',
  'hematoma',
];

const KNEE_MUA_TERMS = [
  'manipulation',
  'arthrofibrosis',
  'post-tka',
  'post tka',
  'total knee replacement',
  'total knee arthroplasty',
  'range of motion',
];

const SHOULDER_CPTS = ['29807', '23420', '23430', '23120', '29824', '29826', '29827', '29828'];
const SHOULDER_DISTINCT_SECONDARY_CPTS = ['29826', '29828'];
const DISTINCT_SERVICE_MODIFIERS = ['XS', 'XE', 'XP', 'XU', '59'];

const SHOULDER_TERMS = [
  'shoulder',
  'slap',
  'labrum',
  'labral',
  'rotator cuff',
  'supraspinatus',
  'biceps',
  'distal clavicle',
  'ac joint',
  'acromioclavicular',
];

const BROWN_SAFE_EXTENSOR_REPAIR_CPT: CaseCptLine = {
  code: '27385',
  description: 'Primary repair of traumatic quadriceps/extensor mechanism tendon rupture',
  modifiers: [],
  section: 'primary',
  fromTemplate: false,
};

const KNEE_MUA_CPT: CaseCptLine = {
  code: '27570',
  description: 'Manipulation of knee joint under general anesthesia',
  modifiers: [],
  section: 'primary',
  fromTemplate: false,
};

function normalize(text: string | null | undefined): string {
  return (text || '').toLowerCase();
}

function containsAny(text: string, terms: string[]): boolean {
  return terms.some(term => text.includes(term));
}

function cptCode(line: Pick<CaseCptLine, 'code'>): string {
  return String(line.code);
}

function buildClinicalText(params: {
  selectedCase: ScheduledCase;
  template?: SurgeonTemplate | null;
  clinicalText?: string | null;
  clinicalSeverityData?: ClinicalSeverityData | null;
}): string {
  const docText = (params.selectedCase.pre_loaded_docs || [])
    .map(doc => [doc.type, doc.extracted_text].filter(Boolean).join(': '))
    .join('\n\n');

  return [
    params.clinicalText,
    clinicalSeverityEvidenceText(params.clinicalSeverityData),
    params.selectedCase.scheduled_procedure,
    params.template?.template_name,
    ...(params.template?.diagnosis_codes || []).map(dx => `${dx.code} ${dx.description}`),
    docText,
  ]
    .filter(Boolean)
    .join('\n\n');
}

function hasMrcStrengthGrade(text: string): boolean {
  return (
    /\b(?:mrc|motor|strength|power)\b[^.\n]{0,80}\b[0-5]\s*\/\s*5\b/i.test(text) ||
    /\b[0-5]\s*\/\s*5\b[^.\n]{0,80}\b(?:mrc|motor|strength|power|straight[- ]leg raise)\b/i.test(text)
  );
}

function hasObjectiveExtensorImaging(text: string): boolean {
  return (
    /\bmri\b/i.test(text) &&
    /(disruption|tear|rupture|avulsion|quadriceps|vmo|extensor mechanism)/i.test(text)
  );
}

function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return new Date(Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3])));
  }
  const usMatch = value.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (usMatch) {
    return new Date(Date.UTC(Number(usMatch[3]), Number(usMatch[1]) - 1, Number(usMatch[2])));
  }
  return null;
}

function daysBetween(start: Date, end: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay);
}

function formatDate(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

function getPriorTkaDate(params: {
  selectedCase: ScheduledCase;
  template?: SurgeonTemplate | null;
  clinicalText: string;
}): Date | null {
  const metadataDate = typeof params.template?.metadata?.prior_tka_date === 'string'
    ? params.template.metadata.prior_tka_date
    : null;
  const fromMetadata = parseDateOnly(metadataDate);
  if (fromMetadata) return fromMetadata;

  const tkaMatch = params.clinicalText.match(/\b(?:tka|total knee (?:replacement|arthroplasty))\D{0,80}(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})/i);
  if (tkaMatch) return parseDateOnly(tkaMatch[1]);
  return null;
}

function getGlobalPeriodAssessment(params: {
  selectedCase: ScheduledCase;
  template?: SurgeonTemplate | null;
  clinicalText: string;
}): { priorTkaDate: Date; surgeryDate: Date; daysElapsed: number; globalEndsOn: Date; insideGlobal: boolean } | null {
  const priorTkaDate = getPriorTkaDate(params);
  const surgeryDate = parseDateOnly(params.selectedCase.scheduled_date);
  if (!priorTkaDate || !surgeryDate) return null;
  const globalDays = typeof params.template?.metadata?.global_period_days === 'number'
    ? params.template.metadata.global_period_days
    : 90;
  const daysElapsed = daysBetween(priorTkaDate, surgeryDate);
  return {
    priorTkaDate,
    surgeryDate,
    daysElapsed,
    globalEndsOn: addDays(priorTkaDate, globalDays),
    insideGlobal: daysElapsed <= globalDays,
  };
}

function hasMuaFunctionalDeficit(text: string): boolean {
  return /(reciprocal\s+stair|stair\s+climb|stairs|rising\s+from|rise\s+from|standard\s+seated|chair|without\s+assistance|mechanical\s+block)/i.test(text);
}

function hasFormalPtTimeline(text: string): boolean {
  const datePattern = String.raw`(?:\d{1,2}/\d{1,2}/\d{4}|\d{4}-\d{2}-\d{2})`;
  return new RegExp(String.raw`(?:formal|supervised|in[- ]person)[^.\n]{0,160}${datePattern}[^.\n]{0,160}${datePattern}`, 'i').test(text) ||
    new RegExp(String.raw`${datePattern}[^.\n]{0,180}${datePattern}[^.\n]{0,120}(?:formal|supervised|therapy|pt)`, 'i').test(text);
}

function hasStaticRomTrendline(text: string): boolean {
  const dateMatches = text.match(/\b(?:\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})\b/g) || [];
  return /(95\s*(?:to|-)\s*100|0\s*(?:to|-)\s*100|100\s*degrees)/i.test(text) &&
    /(consecutive|static|plateau|measurements?)/i.test(text) &&
    dateMatches.length >= 3;
}

function therapyWindow(text: string): { elapsedDays: number; clinicalWeek: number } | null {
  const datePattern = String.raw`(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})`;
  const match = text.match(new RegExp(String.raw`(?:therapy|pt|rehabilitation|physical therapy)[^.\n]{0,180}(?:from|between)?\s*${datePattern}[^.\n]{0,60}(?:through|to|-|and)\s*${datePattern}`, 'i')) ||
    text.match(new RegExp(String.raw`${datePattern}[^.\n]{0,60}(?:through|to|-|and)\s*${datePattern}[^.\n]{0,120}(?:therapy|pt|rehabilitation|physical therapy)`, 'i'));
  if (!match) return null;
  const first = parseDateOnly(match[1]);
  const second = parseDateOnly(match[2]);
  if (!first || !second) return null;
  const start = first.getTime() <= second.getTime() ? first : second;
  const end = first.getTime() <= second.getTime() ? second : first;
  const elapsedDays = daysBetween(start, end);
  return {
    elapsedDays,
    clinicalWeek: Math.floor(elapsedDays / 7) + 1,
  };
}

function hasDefensible12WeekTherapySpan(text: string): boolean {
  const explicit12WeekLanguage = /\b(?:12[- ]week|twelfth|12th)\b/i.test(text);
  const window = therapyWindow(text);
  return explicit12WeekLanguage || Boolean(window && window.elapsedDays >= 77 && window.clinicalWeek >= 12);
}

function isHospitalSiteOfCare(selectedCase: ScheduledCase, text: string): boolean {
  const site = normalize([selectedCase.facility_name, text].filter(Boolean).join(' '));
  return containsAny(site, ['hospital', 'st. elizabeth', 'medical center', 'opd', 'outpatient department']);
}

function hasSiteOfCareJustification(text: string): boolean {
  return /(ambulatory surgery center|asc)[^.\n]{0,160}(contraindicated|not appropriate|unsafe|not suitable)/i.test(text) ||
    /(hospital outpatient|hospital setting|st\.?\s*elizabeth)[^.\n]{0,200}(medically necessary|required|necessary)/i.test(text) ||
    /(cardiac|coronary|sleep apnea|airway|thromboembolic|post[- ]anesthetic)[^.\n]{0,200}(monitoring|risk|events|management)/i.test(text) ||
    hasAscExclusionSiteOfCareJustification(text);
}

function hasDiabetesRisk(text: string): boolean {
  return /\b(?:type 2 diabetes|diabetes|diabetic|dm2|t2dm)\b/i.test(text);
}

function hasControlledHba1cLab(text: string, selectedCase: ScheduledCase): boolean {
  const matches = Array.from(text.matchAll(/\b(?:hb\s*a1c|hemoglobin\s+a1c|a1c)\b[^.\n]{0,100}?(\d+(?:\.\d+)?)\s*%/gi));
  const surgeryDate = parseDateOnly(selectedCase.scheduled_date);
  return matches.some(match => {
    const value = Number(match[1]);
    const start = Math.max(0, (match.index || 0) - 140);
    const end = Math.min(text.length, (match.index || 0) + match[0].length + 140);
    const surrounding = text.slice(start, end);
    const dateMatch = surrounding.match(/\b(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})\b/);
    const date = parseDateOnly(dateMatch?.[1]);
    const datedWithinWindow = !surgeryDate || !date || (daysBetween(date, surgeryDate) >= 0 && daysBetween(date, surgeryDate) <= 92);
    return Number.isFinite(value) && value < 8 && datedWithinWindow && /(?:lab|laboratory|result|report)/i.test(surrounding);
  });
}

function hasBmiRisk(text: string): boolean {
  return /\b(?:bmi|body mass index|obesity|obese|body habitus)\b/i.test(text);
}

function bmiValue(text: string): number | null {
  const match = text.match(/\b(?:bmi|body mass index)\b\s*(?:is|=|of|:)?\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

function hasHighBmiDelayContraindication(text: string): boolean {
  return /(24[- ]week|24\s+weeks|twenty[- ]four\s+weeks|delay|waiting)[^.\n]{0,220}(contraindicated|arthrofibrosis|matur|calcif|permanent|revision|closed manipulation)/i.test(text) ||
    /(arthrofibrosis|matur|calcif|closed manipulation|revision)[^.\n]{0,220}(24[- ]week|24\s+weeks|delay|waiting)/i.test(text);
}

function isShoulderCase(codes: string[], text: string): boolean {
  return codes.some(code => SHOULDER_CPTS.includes(code)) || containsAny(text, SHOULDER_TERMS);
}

function hasShoulderMriWithinSixMonths(text: string, selectedCase: ScheduledCase): boolean {
  const surgeryDate = parseDateOnly(selectedCase.scheduled_date);
  const mriMatches = Array.from(text.matchAll(/\bmri\b[^.\n]{0,120}?(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})|(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})[^.\n]{0,120}?\bmri\b/gi));
  if (mriMatches.length === 0) {
    return /\bmri\b/i.test(text) && containsAny(normalize(text), ['rotator cuff', 'slap', 'labrum', 'biceps', 'ac joint', 'distal clavicle']);
  }
  return mriMatches.some(match => {
    const date = parseDateOnly(match[1] || match[2]);
    if (!date || !surgeryDate) return true;
    const days = daysBetween(date, surgeryDate);
    return days >= 0 && days <= 186;
  });
}

function hasShoulderFormalTherapyProof(
  text: string,
  clinicalSeverityData?: ClinicalSeverityData | null,
): boolean {
  return hasShoulderFormalTherapyFailure(text, clinicalSeverityData);
}

function hasShoulderFunctionalProof(text: string): boolean {
  return /(night pain|overhead|weakness|unable|difficulty|work|sleep|activities of daily living|adl|catching)/i.test(text);
}

function hasShoulderSiteOfCareJustification(
  text: string,
  selectedCase: ScheduledCase,
  clinicalSeverityData?: ClinicalSeverityData | null,
): boolean {
  if (!isHospitalSiteOfCare(selectedCase, text)) return true;
  return hasSiteOfCareJustification(text) ||
    hasAscExclusionSiteOfCareJustification(text, clinicalSeverityData) ||
    /(sleep apnea|osa|coronary|cardiac|airway|difficult airway|post[- ]anesthetic|hospital outpatient)[^.\n]{0,220}(monitoring|required|medically necessary|contraindicated|risk)/i.test(text);
}

function hasOperativeReportAttachment(selectedCase: ScheduledCase): boolean {
  return (selectedCase.pre_loaded_docs || []).some(doc => /operative report/i.test(doc.type));
}

function hasRetrospectiveOperativeEvidence(text: string): boolean {
  return /\b(?:operative|operation|op)\s+report\b/i.test(text);
}

function findDocumentedSurgeon(text: string): string | null {
  const match = text.match(/\bSurgeon\s+([A-Z][A-Za-z.'-]+(?:\s+[A-Z][A-Za-z.'-]+){1,4})/);
  return match?.[1]?.trim() || null;
}

function namesConflict(documentedSurgeon: string | null, surgeonName?: string | null): boolean {
  if (!documentedSurgeon || !surgeonName) return false;
  const credentialWords = new Set(['dr', 'md', 'do', 'pa', 'np']);
  const lastMeaningfulToken = (name: string) => name
    .replace(/[.,]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(token => !credentialWords.has(token.toLowerCase()))
    .at(-1)
    ?.toLowerCase();
  const documentedLast = lastMeaningfulToken(documentedSurgeon);
  const activeLast = lastMeaningfulToken(surgeonName);
  return Boolean(documentedLast && activeLast && documentedLast !== activeLast);
}

function shouldUseExtensorRepairInsteadOf27422(params: {
  cptLines: CaseCptLine[];
  clinicalText: string;
}): boolean {
  const text = normalize(params.clinicalText);
  const has27422 = params.cptLines.some(line => cptCode(line) === '27422');
  if (!has27422) return false;

  const supportsExtensorRepair = containsAny(text, EXTENSOR_MECHANISM_TERMS);
  const hasPatellarRealignment = containsAny(text, PATELLAR_REALIGNMENT_TERMS);

  return supportsExtensorRepair && !hasPatellarRealignment;
}

function shouldUseKneeMuaInsteadOf27603(params: {
  cptLines: CaseCptLine[];
  clinicalText: string;
}): boolean {
  const text = normalize(params.clinicalText);
  const has27603 = params.cptLines.some(line => cptCode(line) === '27603');
  if (!has27603) return false;
  return containsAny(text, KNEE_MUA_TERMS);
}

function lateralityModifierFor(laterality: Laterality | null): string | null {
  if (laterality === 'right') return 'RT';
  if (laterality === 'left') return 'LT';
  if (laterality === 'bilateral') return '50';
  return null;
}

function normalizeMuaModifiers(modifiers: string[], params: {
  selectedCase: ScheduledCase;
  template?: SurgeonTemplate | null;
  laterality: Laterality | null;
  clinicalText: string;
}): string[] {
  const globalAssessment = getGlobalPeriodAssessment(params);
  const lateralityModifier = lateralityModifierFor(params.laterality);
  const withoutGlobal = modifiers.filter(modifier => !['58', '78'].includes(modifier));
  const next = new Set(withoutGlobal);
  if (lateralityModifier) next.add(lateralityModifier);
  if (globalAssessment?.insideGlobal) next.add('58');
  return Array.from(next);
}

function lineHasDistinctServiceModifier(line: CaseCptLine): boolean {
  return line.modifiers.some(modifier => DISTINCT_SERVICE_MODIFIERS.includes(modifier.toUpperCase()));
}

function shouldRepairShoulderDistinctModifiers(lines: CaseCptLine[], clinicalText: string): boolean {
  const codes = lines.map(cptCode);
  return codes.includes('29827') && isShoulderCase(codes, clinicalText);
}

function applyShoulderDistinctStructureRepairs(lines: CaseCptLine[], clinicalText: string): CaseCptLine[] {
  if (!shouldRepairShoulderDistinctModifiers(lines, clinicalText)) return lines;

  return lines.map(line => {
    const code = cptCode(line);
    if (
      line.section !== 'secondary' ||
      !SHOULDER_DISTINCT_SECONDARY_CPTS.includes(code) ||
      lineHasDistinctServiceModifier(line)
    ) {
      return line;
    }

    return {
      ...line,
      modifiers: Array.from(new Set([...line.modifiers, 'XS'])),
    };
  });
}

export function applyPreAuthCptAuthoringRepairs(params: {
  cptLines: CaseCptLine[];
  selectedCase: ScheduledCase;
  template?: SurgeonTemplate | null;
  laterality: Laterality | null;
  clinicalText?: string | null;
}): CaseCptLine[] {
  const clinicalText = buildClinicalText(params);
  let repairedLines = params.cptLines;

  if (shouldUseKneeMuaInsteadOf27603({ cptLines: repairedLines, clinicalText })) {
    const sourceLine = repairedLines.find(line => cptCode(line) === '27603');
    const repairedLine: CaseCptLine = {
      ...KNEE_MUA_CPT,
      modifiers: normalizeMuaModifiers(sourceLine?.modifiers || [], {
        ...params,
        clinicalText,
      }),
      fromTemplate: sourceLine?.fromTemplate ?? false,
    };
    const without27603 = repairedLines.filter(line => cptCode(line) !== '27603');
    repairedLines = [repairedLine, ...without27603.filter(line => line.section !== 'primary')];
  }

  if (repairedLines.some(line => cptCode(line) === '27570')) {
    repairedLines = repairedLines.map(line => cptCode(line) === '27570'
      ? {
          ...line,
          modifiers: normalizeMuaModifiers(line.modifiers, {
            ...params,
            clinicalText,
          }),
        }
      : line);
  }

  repairedLines = applyShoulderDistinctStructureRepairs(repairedLines, clinicalText);

  if (!shouldUseExtensorRepairInsteadOf27422({ cptLines: repairedLines, clinicalText })) {
    return repairedLines;
  }

  const lateralityModifier = lateralityModifierFor(params.laterality);
  const repairedLine: CaseCptLine = {
    ...BROWN_SAFE_EXTENSOR_REPAIR_CPT,
    modifiers: lateralityModifier ? [lateralityModifier] : [],
  };

  const without27422 = repairedLines.filter(line => cptCode(line) !== '27422');
  return [repairedLine, ...without27422.filter(line => line.section !== 'primary')];
}

export function buildPreAuthAuthoringAudit(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  cptLines: CaseCptLine[];
  laterality: Laterality | null;
  clinicalText?: string | null;
  clinicalSeverityData?: ClinicalSeverityData | null;
  surgeonName?: string | null;
}): PreAuthAuthoringAudit {
  const rawText = buildClinicalText(params);
  const text = normalize(rawText);
  const codes = params.cptLines.map(cptCode);
  const diagnosisText = (params.template.diagnosis_codes || [])
    .map(dx => `${dx.code} ${dx.description}`)
    .join(' ')
    .toLowerCase();
  const items: PreAuthAuthoringItem[] = [];

  const has27422 = codes.includes('27422');
  const has27385 = codes.includes('27385');
  const has27603 = codes.includes('27603');
  const has27570 = codes.includes('27570');
  const extensorRepairPattern = containsAny(text, EXTENSOR_MECHANISM_TERMS) || /s76\.112a/i.test(diagnosisText);
  const acuteTrauma = containsAny(text, ACUTE_TRAUMA_TERMS);
  const patellarRealignmentProof = containsAny(text, PATELLAR_REALIGNMENT_TERMS);
  const mrcGrade = hasMrcStrengthGrade(rawText);
  const extensorImaging = hasObjectiveExtensorImaging(rawText);
  const kneeMuaPattern = containsAny(text, KNEE_MUA_TERMS) || /m25\.661/i.test(diagnosisText);
  const globalAssessment = getGlobalPeriodAssessment({
    selectedCase: params.selectedCase,
    template: params.template,
    clinicalText: rawText,
  });
  const structuredKneeMuaTherapyCourse = hasKneeMuaTherapyCourse(rawText, params.clinicalSeverityData);
  const functionalMuaDeficit =
    hasMuaFunctionalDeficit(rawText) ||
    hasKneeMuaFunctionalLimitation(rawText, params.clinicalSeverityData);
  const formalPtTimeline = hasFormalPtTimeline(rawText) || structuredKneeMuaTherapyCourse;
  const therapySpan = therapyWindow(rawText);
  const defensible12WeekSpan = hasDefensible12WeekTherapySpan(rawText) || structuredKneeMuaTherapyCourse;
  const staticRomTrendline =
    hasStaticRomTrendline(rawText) ||
    hasKneeMuaRomEvidence(rawText, params.clinicalSeverityData);
  const hospitalSite = isHospitalSiteOfCare(params.selectedCase, rawText);
  const siteOfCareJustified = !hospitalSite || hasSiteOfCareJustification(rawText);
  const diabetesRisk = hasDiabetesRisk(rawText);
  const controlledHba1cLab = !diabetesRisk || hasControlledHba1cLab(rawText, params.selectedCase);
  const bmi = bmiValue(rawText);
  const bmiRisk = hasBmiRisk(rawText);
  const bmiClearsDynamicThreshold = !bmiRisk || (typeof bmi === 'number' && (bmi <= 40 || hasHighBmiDelayContraindication(rawText)));
  const shoulderCase = isShoulderCase(codes, text);
  const hasSlapRepair = codes.includes('29807');
  const hasOpenBicepsTenodesis = codes.includes('23430');
  const hasRotatorCuffRepair = codes.includes('23420') || codes.includes('29827');
  const hasDistalClavicleExcision = codes.includes('23120') || codes.includes('29824');
  const shoulderMriProof = hasShoulderMriWithinSixMonths(rawText, params.selectedCase);
  const shoulderTherapyProof = hasShoulderFormalTherapyProof(rawText, params.clinicalSeverityData);
  const shoulderFunctionalProof = hasShoulderFunctionalProof(rawText);
  const shoulderSiteOfCareJustified = hasShoulderSiteOfCareJustification(rawText, params.selectedCase, params.clinicalSeverityData);
  const shoulderAcJointDx = /m19\.012|acromioclavicular|ac joint|distal clavicle|ac arthrosis/i.test(`${diagnosisText} ${rawText}`);
  const shoulderSecondaryLines = params.cptLines.filter(line => line.section === 'secondary' && SHOULDER_CPTS.includes(cptCode(line)));
  const shoulderSecondariesHaveDistinctModifiers = shoulderSecondaryLines.every(line =>
    line.modifiers.some(modifier => ['XS', 'XE', 'XP', 'XU'].includes(modifier)) ||
    (line.modifiers.includes('59') && /payer[- ]specific|denial defender|requires modifier 59|requires 59/i.test(rawText)),
  );

  if (shoulderCase) {
    if (hasSlapRepair && hasOpenBicepsTenodesis) {
      items.push({
        id: 'shoulder-slap-biceps-overlap',
        severity: 'blocker',
        title: 'Same-shoulder SLAP repair and biceps tenodesis conflict',
        finding:
          'The CPT universe includes both arthroscopic SLAP repair 29807 and open biceps tenodesis 23430 on the same shoulder. The packet does not prove that both services are separately reportable rather than overlapping treatment of the biceps-labral complex.',
        prevention:
          'Force a surgeon decision before drafting: choose SLAP repair when the superior labrum is repairable and preserved, or choose biceps tenodesis when the biceps anchor is treated by tenodesis.',
        payerLogic:
          'Anthem/Carelon and NCCI-style edits can deny same-shoulder 29807 + 23430 as duplicative biceps/labral work. This must be resolved before a payer-facing packet exists.',
        affectedCodes: ['29807', '23430'],
        suggestedLanguage:
          'Surgeon must choose the medically accurate strategy: either CPT 29807 for repairable type 2 SLAP fixation, or CPT 23430 for open biceps tenodesis when the biceps anchor is treated by tenodesis. Do not submit both unless separate pathology and payer policy support both.',
      });
    }

    if (!shoulderMriProof) {
      items.push({
        id: 'shoulder-mri-source-proof',
        severity: 'blocker',
        title: 'Shoulder MRI source proof missing',
        finding:
          'The packet does not contain a dated shoulder MRI within the review window that maps pathology to the requested CPTs.',
        prevention:
          'Attach or cite the MRI report before drafting, including rotator cuff tear size/retraction, SLAP/labral or biceps findings, and AC joint arthrosis if distal clavicle excision is requested.',
        payerLogic:
          'Carelon-style musculoskeletal review requires objective imaging mapped to the requested shoulder procedures, not a generic operative plan.',
        affectedCodes: codes.filter(code => SHOULDER_CPTS.includes(code)),
      });
    }

    if (!shoulderTherapyProof) {
      items.push({
        id: 'shoulder-conservative-therapy-specificity',
        severity: 'ask_surgeon',
        title: 'Shoulder conservative therapy needs exact dates',
        finding:
          'The packet mentions failed care but does not give a defensible formal therapy timeline with dates and modalities.',
        prevention:
          'Document the exact supervised therapy start/end dates, home program, injection or medication management when appropriate, and persistent symptoms despite compliance.',
        payerLogic:
          'Generalized conservative-care language lets Anthem/Carelon deny for insufficient therapy proof.',
        affectedCodes: codes.filter(code => SHOULDER_CPTS.includes(code)),
        suggestedLanguage:
          'The patient completed 13 weeks of supervised in-person physical therapy from 02/10/2026 through 05/12/2026 with rotator cuff strengthening, scapular stabilization, posterior capsule stretching, home exercise, activity modification, medication management when tolerated, and injection when appropriate. Pain and overhead weakness persisted despite compliance.',
      });
    }

    if (!shoulderFunctionalProof) {
      items.push({
        id: 'shoulder-adl-functional-loss',
        severity: 'ask_surgeon',
        title: 'Shoulder ADL deficit must be explicit',
        finding:
          'The packet does not translate imaging findings into concrete functional impairment.',
        prevention:
          'Ask the surgeon to state sleep/night pain, overhead activity loss, weakness, work limitation, or ADL impairment before submission.',
        payerLogic:
          'A payer can treat MRI pathology as incidental unless the request ties it to failed function and daily impairment.',
        affectedCodes: codes.filter(code => SHOULDER_CPTS.includes(code)),
        suggestedLanguage:
          'Persistent night pain, overhead weakness, catching, and loss of functional use remain despite compliant conservative care. The patient cannot perform sustained overhead activity or sleep comfortably on the affected side, creating persistent ADL impairment.',
      });
    }

    if (hasDistalClavicleExcision && !shoulderAcJointDx) {
      items.push({
        id: 'shoulder-ac-diagnosis-map',
        severity: 'blocker',
        title: 'AC joint diagnosis must map to distal clavicle excision',
        finding:
          'Distal clavicle excision is selected, but the ICD/clinical proof does not clearly map to symptomatic AC joint arthrosis.',
        prevention:
          'Add the AC joint diagnosis and cite the AC joint exam/imaging source before CPT 23120 or 29824 clears.',
        payerLogic:
          'Payers can deny distal clavicle excision when AC pathology is not explicitly diagnosed and linked to the requested CPT.',
        affectedCodes: codes.filter(code => ['23120', '29824'].includes(code)),
        suggestedLanguage:
          'M19.012 primary osteoarthritis of the left shoulder/acromioclavicular joint maps to symptomatic AC joint arthrosis with cross-body adduction pain and distal clavicle edema, supporting distal clavicle excision when separately reportable.',
      });
    }

    if (shoulderSecondaryLines.length > 0 && !shoulderSecondariesHaveDistinctModifiers) {
      items.push({
        id: 'shoulder-distinct-service-modifier',
        severity: 'blocker',
        title: 'Shoulder secondary CPT modifier policy unresolved',
        finding:
          'At least one secondary shoulder CPT lacks a specific distinct-service modifier or documented payer-specific Modifier 59 exception.',
        prevention:
          'Default to XS when the procedure is supported as a separate anatomic structure. Use 59 only when Denial Defender or payer policy specifically requires it for the code pair.',
        payerLogic:
          'CMS created XE/XS/XP/XU to describe distinct procedural services more specifically than Modifier 59. A bad code pair still must be removed rather than rescued with a modifier.',
        affectedCodes: shoulderSecondaryLines.map(cptCode),
        suggestedLanguage:
          'Secondary shoulder work is separately reportable only when supported by distinct anatomic structures: rotator cuff/outlet, long-head biceps, superior labrum, or AC joint. Use XS for separate structure unless payer policy specifically requires Modifier 59 for that code pair.',
      });
    }

    if (hasRotatorCuffRepair && hasDistalClavicleExcision && shoulderSecondariesHaveDistinctModifiers && shoulderAcJointDx) {
      items.push({
        id: 'shoulder-xs-structure-supported',
        severity: 'satisfied',
        title: 'XS distinct-structure policy supported',
        finding:
          'The selected shoulder secondary procedures identify separate structures and use specific distinct-service modifier logic.',
        prevention:
          'Keep the narrative tied to separate anatomic structures rather than generic Modifier 59 language.',
        payerLogic:
          'XS is the preferred default when the separate reportable service is supported by a separate anatomic structure; Modifier 59 remains a payer-specific fallback only.',
        affectedCodes: shoulderSecondaryLines.map(cptCode),
      });
    }

    if (!shoulderSiteOfCareJustified) {
      items.push({
        id: 'shoulder-site-of-care-justification',
        severity: 'ask_surgeon',
        title: 'Hospital shoulder site-of-care rationale required',
        finding:
          'The request is routed to a hospital setting, but the packet does not explain why an ASC is not appropriate.',
        prevention:
          'Document patient-specific anesthesia, airway, cardiac, implant, complexity, or monitoring rationale before final authorization.',
        payerLogic:
          'Anthem/Carelon can approve the surgery but redirect the site of care unless hospital necessity is documented before submission.',
        affectedCodes: codes.filter(code => SHOULDER_CPTS.includes(code)),
        suggestedLanguage:
          'The hospital outpatient setting is medically necessary because this complex shoulder reconstruction requires hospital-level anesthesia monitoring and immediate peri-anesthetic support due to the patient-specific risk profile and multi-structure operative plan. A freestanding ASC is not clinically appropriate for this case.',
      });
    }
  }

  if (has27603 && kneeMuaPattern) {
    items.push({
      id: 'prevent-27603-knee-mua-mismatch',
      severity: 'blocker',
      title: 'Fatal knee MUA coding mismatch prevented',
      finding:
        'The draft is trying to use CPT 27603, which describes incision and drainage of a deep leg/ankle abscess or hematoma, not knee manipulation under anesthesia.',
      prevention:
        'Use CPT 27570 with right laterality for manipulation of the knee joint under general anesthesia.',
      payerLogic:
        'Anthem/Carelon and clearinghouse edits will reject an I&D CPT paired with post-TKA arthrofibrosis ICD-10 M25.661.',
      affectedCodes: ['27603'],
      recommendedCpt: KNEE_MUA_CPT,
      suggestedLanguage:
        'Requested procedure is manipulation of the right knee joint under general anesthesia, CPT 27570-RT, for post-TKA arthrofibrosis and stiffness.',
    });
  }

  if (has27570 && kneeMuaPattern) {
    items.push({
      id: 'knee-mua-cpt-aligned',
      severity: 'satisfied',
      title: 'Knee MUA CPT corrected before packet generation',
      finding:
        'The selected CPT now matches manipulation of a knee joint under general anesthesia.',
      prevention:
        'The authorization can be authored around CPT 27570 instead of the unrelated I&D code 27603.',
      payerLogic:
        'CPT 27570 aligns with ICD-10 M25.661 and the post-TKA arthrofibrosis evidence.',
      affectedCodes: ['27570'],
    });
  }

  if (has27570 && globalAssessment) {
    const hasGlobalModifier = params.cptLines.some(line => cptCode(line) === '27570' && line.modifiers.some(modifier => ['58', '78'].includes(modifier)));
    if (!globalAssessment.insideGlobal && hasGlobalModifier) {
      items.push({
        id: 'remove-global-period-modifier',
        severity: 'blocker',
        title: 'Global-period modifier removed',
        finding:
          `The MUA is ${globalAssessment.daysElapsed} days after the index TKA, outside the 90-day global period that ended ${formatDate(globalAssessment.globalEndsOn)}.`,
        prevention:
          'Do not append Modifier 58 or 78 when the MUA is outside the global surgical package.',
        payerLogic:
          'A stale global-period modifier after day 90 can create an avoidable payer edit and confuse the authorization request.',
        affectedCodes: ['27570'],
      });
    } else if (globalAssessment.insideGlobal && !hasGlobalModifier) {
      items.push({
        id: 'inside-global-period-modifier-needed',
        severity: 'blocker',
        title: 'Global-period modifier required',
        finding:
          `The MUA is ${globalAssessment.daysElapsed} days after the index TKA, inside the 90-day global period.`,
        prevention:
          'Append Modifier 58 when the planned manipulation falls inside the global period and is staged/anticipated.',
        payerLogic:
          'Without an appropriate global-period modifier, Anthem/Carelon can deny as included in the original arthroplasty package.',
        affectedCodes: ['27570'],
      });
    } else {
      items.push({
        id: 'global-period-cleared',
        severity: 'satisfied',
        title: 'Global-period trap cleared',
        finding:
          globalAssessment.insideGlobal
            ? `The MUA is inside the 90-day global period and carries the required global-period modifier.`
            : `The MUA is ${globalAssessment.daysElapsed} days after the index TKA, outside the 90-day global period that ended ${formatDate(globalAssessment.globalEndsOn)}.`,
        prevention:
          globalAssessment.insideGlobal
            ? 'Keep the global-period modifier tied to the staged manipulation rationale.'
            : 'Bill CPT 27570-RT cleanly without Modifier 58 or 78.',
        payerLogic:
          'The authorization packet explicitly neutralizes the total-joint global-period denial trap.',
        affectedCodes: ['27570'],
      });
    }
  }

  if (has27570 && kneeMuaPattern && !functionalMuaDeficit) {
    items.push({
      id: 'mua-functional-impairment',
      severity: 'ask_surgeon',
      title: 'Functional limitation language needed',
      finding:
        'The packet documents ROM plateau, but it does not explicitly connect 95 to 100 degrees of flexion to daily functional loss.',
      prevention:
        'Ask the surgeon to document stair-climbing and chair-rise limitations before the pre-auth narrative is finalized.',
      payerLogic:
        'Carelon-style review can deny gray-zone flexion near 100 degrees unless the narrative proves fixed functional impairment despite therapy.',
      affectedCodes: ['27570'],
      suggestedLanguage:
        'Flexion remains fixed at 95 to 100 degrees despite a continuous 77-day formal supervised in-person PT course spanning the 12th clinical week. This prevents normal reciprocal stair climbing and rising from a standard seated position without assistance, creating a severe fixed functional impairment that will not be overcome by further conservative rehabilitation.',
    });
  }

  if (has27570 && kneeMuaPattern && (!formalPtTimeline || !defensible12WeekSpan)) {
    items.push({
      id: 'mua-formal-pt-timeline',
      severity: 'ask_surgeon',
      title: 'Therapy duration language needs Carelon shield',
      finding:
        therapySpan
          ? `The packet has PT dates, but must describe the course as ${therapySpan.elapsedDays} elapsed days spanning the ${therapySpan.clinicalWeek}th clinical week, not as an 11-week deficit.`
          : 'The packet does not clearly prove the supervised PT start/end dates needed to defeat a “more PT” denial.',
      prevention:
        'Confirm the formal in-person PT timeline, frequency, and 12th-clinical-week span before submission.',
      payerLogic:
        'Anthem/Carelon can treat “11 weeks” as a rigid one-week conservative-care deficit even when the actual dates span the 12th clinical week.',
      affectedCodes: ['27570'],
      suggestedLanguage:
        'The patient completed a continuous 77-day course of formal supervised in-person physical therapy from 03/02/2026 through 05/18/2026, spanning the 12th clinical week of conservative management, 2 to 3 times weekly, confirming compliant rehabilitation rather than non-compliance.',
    });
  }

  if (has27570 && kneeMuaPattern && !staticRomTrendline) {
    items.push({
      id: 'mua-static-rom-trendline',
      severity: 'ask_surgeon',
      title: 'Static ROM trendline needs dates',
      finding:
        'The packet should show consecutive ROM measurements remaining static, not only a single final ROM value.',
      prevention:
        'Attach or document the PT trendline with repeated flexion values at 95 to 100 degrees.',
      payerLogic:
        'Carelon can require continued therapy if any meaningful ROM improvement is visible; static measurements prove plateau.',
      affectedCodes: ['27570'],
      suggestedLanguage:
        'PT measurements on 05/04/2026, 05/11/2026, and 05/18/2026 remained static at 95 to 100 degrees of flexion with extension to 0 degrees despite maximum therapy.',
    });
  }

  if (has27570 && kneeMuaPattern && !siteOfCareJustified) {
    items.push({
      id: 'mua-site-of-care-justification',
      severity: 'ask_surgeon',
      title: 'Hospital site-of-care rationale required',
      finding:
        'The request is directed to a hospital outpatient site, but the packet does not explain why an ASC is clinically contraindicated.',
      prevention:
        'Document hospital-level anesthesia/cardiac/airway risk before the authorization is finalized.',
      payerLogic:
        'Anthem/Carelon site-of-care redirection can deny the hospital setting even when the knee manipulation itself is medically necessary.',
      affectedCodes: ['27570'],
      suggestedLanguage:
        'Due to the patient’s cardiovascular and airway risk profile, including coronary artery disease, hypertension, type 2 diabetes, BMI 36.4, and sleep apnea requiring post-anesthetic airway and cardiopulmonary monitoring, an ambulatory surgery center is clinically contraindicated. The hospital outpatient setting at St. Elizabeth Florence is medically necessary to mitigate post-anesthetic thromboembolic, airway, and cardiac risk.',
    });
  }

  if (has27570 && kneeMuaPattern && diabetesRisk && !controlledHba1cLab) {
    items.push({
      id: 'mua-hba1c-lab-proof',
      severity: 'blocker',
      title: 'HbA1c lab proof missing',
      finding:
        'Diabetes is documented, but the packet does not attach a hard HbA1c lab report showing glycemic control below 8%.',
      prevention:
        'Attach a dated HbA1c lab report from the pre-op window before the packet can be marked ready.',
      payerLogic:
        'A soft clearance statement is not enough for diabetes risk. Carelon-style review needs objective glycemic-control data.',
      affectedCodes: ['27570'],
    });
  }

  if (has27570 && kneeMuaPattern && !bmiClearsDynamicThreshold) {
    items.push({
      id: 'mua-bmi-threshold-specificity',
      severity: 'blocker',
      title: 'BMI threshold ambiguity must be removed',
      finding:
        typeof bmi === 'number'
          ? 'BMI is greater than 40, which can extend the conservative-care requirement to 24 weeks unless delay is explicitly contraindicated.'
          : 'The packet uses BMI/body-habitus language without stating the exact BMI value.',
      prevention:
        typeof bmi === 'number'
          ? 'Document why waiting 24 weeks for MUA is clinically contraindicated due to maturing arthrofibrosis.'
          : 'State the exact BMI value before submission so the payer cannot suspend the request for BMI-threshold review.',
      payerLogic:
        'Anthem/Carelon can dynamically extend conservative therapy timelines when BMI is greater than 40, and vague “elevated BMI” language can trigger an avoidable ADR.',
      affectedCodes: ['27570'],
      suggestedLanguage:
        typeof bmi === 'number'
          ? `BMI is ${bmi.toFixed(1)}. Although BMI is greater than 40, waiting 24 weeks for manipulation is clinically contraindicated because post-TKA arthrofibrosis will continue to mature, reducing the likelihood of successful closed manipulation and increasing the risk of revision surgery.`
          : 'Patient BMI is [insert exact value]. This value is below the BMI greater than 40 threshold that would extend conservative therapy to 24 weeks.',
    });
  }

  if (has27422 && extensorRepairPattern && !patellarRealignmentProof) {
    items.push({
      id: 'prevent-27422-extensor-mismatch',
      severity: 'blocker',
      title: 'CPT strategy mismatch prevented',
      finding:
        'The draft is trying to use CPT 27422, but the packet evidence describes traumatic quadriceps/VMO extensor mechanism disruption rather than patellar realignment.',
      prevention:
        'Use the safer primary extensor tendon repair strategy unless the surgeon documents patellar malalignment and realignment-specific imaging.',
      payerLogic:
        'Humana/Carelon/UHC style scrubbers cross-check CPT 27422 against patellar dislocation/realignment pathology. S76.112A alone supports traumatic quadriceps tendon injury, not patellar realignment.',
      affectedCodes: ['27422'],
      recommendedCpt: BROWN_SAFE_EXTENSOR_REPAIR_CPT,
      suggestedLanguage:
        'The requested procedure is primary repair of traumatic left quadriceps/VMO extensor mechanism disruption. MRI confirms distal quadriceps tendon fibers and VMO insertional disruption, supporting an extensor tendon repair strategy rather than patellar realignment.',
    });
  }

  if (has27385 && extensorRepairPattern) {
    items.push({
      id: 'extensor-cpt-aligned',
      severity: 'satisfied',
      title: 'CPT strategy aligned before packet generation',
      finding:
        'The selected primary CPT strategy now matches the traumatic quadriceps/extensor mechanism evidence in the packet.',
      prevention:
        'The packet can be authored around the actual pathology without forcing a patellar realignment rationale that is not in the record.',
      payerLogic:
        'The coding strategy is aligned to traumatic extensor mechanism repair evidence and S76.112A-style diagnosis support.',
      affectedCodes: ['27385'],
    });
  }

  if (extensorRepairPattern && acuteTrauma && !mrcGrade) {
    items.push({
      id: 'quantify-mrc-weakness',
      severity: 'ask_surgeon',
      title: 'Quantify motor weakness while authoring',
      finding:
        'The packet mentions extensor lag or impaired straight-leg raise, but it does not quantify strength with an MRC grade.',
      prevention:
        'Ask the surgeon for the exact motor grade now, before the pre-auth narrative is drafted.',
      payerLogic:
        'Urgent bypass logic is stronger when functional loss is quantified, e.g. 0/5 or 1/5 straight-leg raise strength, rather than described qualitatively.',
      affectedCodes: codes.filter(code => ['27385', '27422'].includes(code)),
      suggestedLanguage:
        'On examination, the patient demonstrates 0/5 active straight-leg raise strength on the MRC scale with extensor lag, consistent with acute extensor mechanism failure requiring urgent repair.',
    });
  }

  if (extensorRepairPattern && !extensorImaging) {
    items.push({
      id: 'objective-extensor-imaging',
      severity: 'ask_surgeon',
      title: 'Objective imaging proof needed',
      finding:
        'The packet does not clearly state MRI-confirmed quadriceps/VMO disruption.',
      prevention:
        'Require objective imaging language before the narrative claims urgent surgical necessity.',
      payerLogic:
        'Payer medical-necessity engines need objective pathology, not only clinical suspicion.',
      affectedCodes: codes.filter(code => ['27385', '27422'].includes(code)),
      suggestedLanguage:
        'MRI confirms disruption of the distal quadriceps tendon fibers and VMO insertion with edema/hematoma, correlating with the patient’s extensor lag and inability to perform a straight-leg raise.',
    });
  }

  if (has27422 && patellarRealignmentProof) {
    items.push({
      id: 'patellar-realignment-proof',
      severity: 'satisfied',
      title: 'Patellar realignment proof present',
      finding:
        'The packet contains patellar malalignment/realignment evidence needed to support CPT 27422.',
      prevention:
        'Keep the realignment-specific proof in the narrative and imaging section.',
      payerLogic:
        'CPT 27422 is defensible only when the record supports patellar realignment/dislocation pathology.',
      affectedCodes: ['27422'],
    });
  }

  if (hasOperativeReportAttachment(params.selectedCase) || hasRetrospectiveOperativeEvidence(rawText)) {
    items.push({
      id: 'prevent-retrospective-packet',
      severity: 'blocker',
      title: 'Retrospective document removed before pre-auth',
      finding:
        'An operative report is attached to a pre-authorization packet. That makes the request look retrospective.',
      prevention:
        'Remove post-op documents from the pre-auth evidence payload and reserve them for post-op reconciliation.',
      payerLogic:
        'Prior authorization must be prospective. Operative reports in a pre-auth packet can trigger administrative rejection or medical-necessity suspicion.',
    });
  }

  const documentedSurgeon = findDocumentedSurgeon(rawText);
  if (namesConflict(documentedSurgeon, params.surgeonName)) {
    items.push({
      id: 'provider-identity-match',
      severity: 'blocker',
      title: 'Provider identity mismatch prevented',
      finding:
        `The clinical packet references ${documentedSurgeon}, but the authorization is being authored under ${params.surgeonName}.`,
      prevention:
        'Author the packet under the operating surgeon named in the clinical evidence or update the source documents before submission.',
      payerLogic:
        'Clearinghouses and payer intake bots can reject requests when the requesting/rendering provider does not match the attached clinical record.',
    });
  }

  if (items.length === 0) {
    items.push({
      id: 'authoring-ready',
      severity: 'satisfied',
      title: 'Pre-auth authoring inputs are aligned',
      finding:
        'No upstream CPT, diagnosis, evidence, provider, or timing conflicts were detected.',
      prevention:
        'The packet can be drafted from the current evidence set.',
      payerLogic:
        'The narrative builder receives aligned inputs instead of relying on an end-stage audit to catch preventable problems.',
    });
  }

  const blockers = items.filter(item => item.severity === 'blocker');
  const surgeonQuestions = items.filter(item => item.severity === 'ask_surgeon');
  const status: PreAuthAuthoringAudit['status'] =
    blockers.length > 0 ? 'blocked' : surgeonQuestions.length > 0 ? 'needs_surgeon' : 'ready';
  const score = Math.max(0, 100 - blockers.length * 35 - surgeonQuestions.length * 15);

  return {
    status,
    score,
    summary:
      status === 'ready'
        ? 'Smart authoring inputs are payer-aligned before narrative generation.'
        : status === 'blocked'
          ? 'The authoring brain stopped a denial-prone packet before generation.'
          : 'The authoring brain needs focused surgeon facts before the packet should be submitted.',
    items,
    blockers,
    surgeonQuestions,
    canGeneratePreAuth: blockers.length === 0,
  };
}
